import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Receipt } from 'lucide-react';
import {
  PageHeader,
  DashboardGrid,
  DashboardCard,
  StatusBadge,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import {
  useContract,
  useContractMilestones,
  useContractTransition,
  useAcceptMilestone,
  useBillMilestone,
} from '@/hooks/useContracts';
import { useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { contractStatusVariants, milestoneStatusVariants } from '@/config/contract-status';
import { formatMoney, shortDate } from '@/lib/format';
import type { KoshContractStatus } from '@/types/contracts';

const NEXT: Partial<Record<KoshContractStatus, { to: KoshContractStatus; label: string }>> = {
  DRAFT: { to: 'PENDING_APPROVAL', label: 'Submit for approval' },
  PENDING_APPROVAL: { to: 'ACTIVE', label: 'Activate' },
  ACTIVE: { to: 'COMPLETED', label: 'Mark complete' },
};

export function ContractDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useContract(id);
  const milestonesQ = useContractMilestones(id);
  const nameMap = useCustomerNameMap();
  const transition = useContractTransition();
  const accept = useAcceptMilestone();
  const bill = useBillMilestone();

  return (
    <div className="space-y-6">
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError}>
        {(c) => {
          const next = NEXT[c.status];
          const isActive = c.status === 'ACTIVE';
          const milestones = milestonesQ.data ?? [];
          return (
            <>
              <PageHeader
                eyebrow={
                  <Link to="/contracts" className="inline-flex items-center gap-1 hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5" /> Contracts
                  </Link>
                }
                title={c.contractNumber}
                meta={<StatusBadge status={c.status} variants={contractStatusVariants} />}
                actions={
                  next ? (
                    <Button
                      size="sm"
                      disabled={transition.isPending}
                      onClick={() => id && transition.mutate({ id, to: next.to })}
                    >
                      {next.label}
                    </Button>
                  ) : undefined
                }
              />

              <DashboardGrid columns={2}>
                <DashboardCard title="Details">
                  <div className="space-y-1.5 text-[13px]">
                    <Row k="Customer" v={nameMap.get(c.customerId) ?? c.customerId.slice(0, 8)} />
                    <Row k="Type" v={c.contractType} />
                    <Row k="Billing" v={c.billingSchedule ?? '—'} />
                    <Row k="Value" v={formatMoney(c.value, c.currency)} />
                    <Row k="Created" v={shortDate(c.createdAt)} />
                    {c.notes && <Row k="Notes" v={c.notes} />}
                  </div>
                </DashboardCard>
                <DashboardCard title="Lifecycle">
                  <p className="text-[13px] text-muted-foreground">
                    {c.status === 'DRAFT' && 'Submit for approval, then activate to bill milestones.'}
                    {c.status === 'PENDING_APPROVAL' && 'Activate the contract to start billing milestones.'}
                    {isActive && 'Active — accept and bill milestones below. Each bill issues an invoice.'}
                    {['COMPLETED', 'CLOSED', 'TERMINATED', 'EXPIRED'].includes(c.status) &&
                      'This contract is closed.'}
                  </p>
                </DashboardCard>
              </DashboardGrid>

              <DashboardCard title="Milestones" flushBody>
                {milestones.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No milestones on this contract.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Milestone</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Due</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {milestones.map((m) => {
                        const billable = isActive && m.status !== 'BILLED' && m.status !== 'PAID';
                        return (
                          <TableRow key={m.id}>
                            <TableCell className="font-medium">{m.label}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatMoney(m.amountMinor, c.currency)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {m.dueDate ? shortDate(m.dueDate) : '—'}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={m.status} variants={milestoneStatusVariants} />
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="inline-flex gap-1.5">
                                {isActive && m.status === 'PLANNED' && (
                                  <button
                                    onClick={() => id && accept.mutate({ id, mid: m.id })}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] text-muted-foreground hover:bg-muted"
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Accept
                                  </button>
                                )}
                                {billable && (
                                  <button
                                    onClick={() => id && bill.mutate({ id, mid: m.id })}
                                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11.5px] text-[hsl(142_70%_35%)] hover:bg-muted"
                                  >
                                    <Receipt className="h-3.5 w-3.5" /> Bill
                                  </button>
                                )}
                                {(m.status === 'BILLED' || m.status === 'PAID') && (
                                  <span className="text-[11.5px] text-muted-foreground">Invoiced</span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </DashboardCard>
            </>
          );
        }}
      </QueryStateGuard>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}
